import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Dialog, DialogContent, Theme } from "@mui/material";
import { LoadingIcon } from "@shared/Loading";

import { Tooltip } from "./Tooltip";

const useStyles = makeStyles()((theme: Theme) => ({
    dialogContent: {
        height: "calc(80vh - 24px)",
        background: "black",
        color: "white",
        overflow: "auto",
        padding: theme.spacing(5),
        flexGrow: 1,
    },
    modalTitleBox: {
        display: "flex",
        justifyContent: "space-between",
        color: "white",
        backgroundColor: "black",
        padding: theme.spacing(5),
    },
    modalBg: {
        backgroundColor: "black",
        color: "white",
    },
    titleLeftContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(5),
    },
    headerIconBtn: {
        height: 30,
        width: 30,
        cursor: "pointer",
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
        color: "white",

        "&:disabled": {
            cursor: "default",
            opacity: 0.5,
        },
    },
    logs: {
        color: "white",
        overflow: "visible",
        fontFamily: "monospace",
    },
    contentContainer: {
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    statusIndicator: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    statusDot: {
        width: 8,
        height: 8,
        borderRadius: "50%",
    },
    statusConnected: {
        backgroundColor: theme.palette.emteria.success,
    },
    statusDisconnected: {
        backgroundColor: theme.palette.emteria.text.secondary,
    },
}));

type StreamStatusIndicatorProps = {
    isStreamActive?: boolean;
};

const StreamStatusIndicator = ({ isStreamActive }: StreamStatusIndicatorProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    if (isStreamActive === undefined) {
        return null;
    }

    const statusText = isStreamActive ? t("common:status.connected") : t("common:status.disconnected");

    return (
        <div className={classes.statusIndicator}>
            <div className={clsx(classes.statusDot, isStreamActive ? classes.statusConnected : classes.statusDisconnected)} />
            <span>{statusText}</span>
        </div>
    );
};

export type StreamStatus = "initializing" | "streaming" | "connected" | "done" | null;

type Props = {
    status: StreamStatus;
    downloadDisabled?: boolean;
    downloading?: boolean;
    onDownload?: () => void;
    onCopyLink?: () => void;
    title: string;
    open: boolean;
    onClose: () => void;
    isStreamActive?: boolean;
    containerRef: React.RefObject<HTMLDivElement | null>;
    contentRef: React.RefObject<HTMLPreElement | null>;
};

export const LogStreamModal = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);

    const isStreamActive = props.status === "connected" || props.status === "streaming";

    return (
        <Dialog open={props.open} fullScreen disableEscapeKeyDown>
            <DialogContent className={classes.contentContainer}>
                <div className={classes.modalTitleBox}>
                    <div className={classes.titleLeftContainer}>
                        <div className={classes.modalBg}>{props.title}</div>
                        <StreamStatusIndicator isStreamActive={isStreamActive} />
                        {props.onDownload ? (
                            <Tooltip title={t("modals:logStreamer.downloadLogs")}>
                                {!props.downloading ? (
                                    <button
                                        disabled={props.status === "streaming" || props.downloadDisabled}
                                        className={clsx("fa-solid fa-download", classes.headerIconBtn)}
                                        onClick={props.onDownload}
                                    />
                                ) : (
                                    <LoadingIcon inverse />
                                )}
                            </Tooltip>
                        ) : null}
                        {props.onCopyLink ? (
                            <Tooltip title={t("modals:logStreamer.copyLink")}>
                                <button className={clsx("fa-solid fa-link", classes.headerIconBtn)} onClick={props.onCopyLink} />
                            </Tooltip>
                        ) : null}
                    </div>
                    <div className={clsx("fa-solid fa-xmark", classes.headerIconBtn)} onClick={props.onClose} />
                </div>
                <div ref={props.containerRef} className={classes.dialogContent}>
                    <pre className={classes.logs} ref={props.contentRef} />

                    {props.status === "initializing" ? <LoadingIcon size="block" inverse /> : null}

                    {props.status === "connected" ? <div>{t("modals:logStreamer.waitingForLogs")}</div> : null}

                    {props.status === "done" && !props.contentRef.current?.textContent ? (
                        <div>{t("modals:logStreamer.noLogs")}</div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};
